class SellingPlansWidget {
  constructor(sellingPlansWidgetContainer) {
    this.enablePerformanceObserver();
    this.sellingPlansWidgetContainer = sellingPlansWidgetContainer;
    this.hiddenClass = "selling_plan_theme_integration--hidden";
    
    // Initialize in correct order
    this.appendSellingPlanInputs();
    this.handleSellingPlanFormVisibility();
    this.updateSellingPlanInputsValues();
    this.listenToVariantChange();
    this.listenToSellingPlanFormRadioButtonChange();
    
    // Set subscription as default and update price on page load
    this.setDefaultSubscription();
    this.updatePrice();
  }

  get sectionId() {
    return this.sellingPlansWidgetContainer.getAttribute("data-section-id");
  }

  get shopifySection() {
    return document.querySelector(`#shopify-section-${this.sectionId}`);
  }

  get variantIdInput() {
    return (
      this.addToCartForm?.querySelector(`input[name="id"]`) ||
      this.addToCartForm?.querySelector(`select[name="id"]`)
    );
  }

  get formId() {
    return this.sellingPlansWidgetContainer.getAttribute("data-form-id");
  }

  get priceElement() {
    return this.shopifySection?.querySelector(".price");
  }

  get visibleSellingPlanForm() {
    if (!this.variantIdInput) return null;
    return this.shopifySection?.querySelector(
      `section[data-variant-id="${this.variantIdInput.value}"]`
    );
  }

  get sellingPlanInput() {
    return this.shopifySection?.querySelector(".selected-selling-plan-id");
  }

  get addToCartForm() {
    return this.shopifySection?.querySelector(`#${this.formId}`);
  }

  appendSellingPlanInputs() {
    if (this.addToCartForm && this.sellingPlanInput) {
      this.addToCartForm.appendChild(this.sellingPlanInput.cloneNode());
    }
  }

  showSellingPlanForm(sellingPlanFormForSelectedVariant) {
    sellingPlanFormForSelectedVariant?.classList?.remove(this.hiddenClass);
  }

  hideSellingPlanForms(sellingPlanFormsForUnselectedVariants) {
    sellingPlanFormsForUnselectedVariants?.forEach((element) => {
      element.classList.add(this.hiddenClass);
    });
  }

  handleSellingPlanFormVisibility() {
    if (!this.variantIdInput) return;
    
    const sellingPlanFormForSelectedVariant = this.shopifySection?.querySelector(
      `section[data-variant-id="${this.variantIdInput.value}"]`
    );
    const sellingPlanFormsForUnselectedVariants =
      this.shopifySection?.querySelectorAll(
        `.selling_plan_theme_integration:not([data-variant-id="${this.variantIdInput.value}"])`
      );
    this.showSellingPlanForm(sellingPlanFormForSelectedVariant);
    this.hideSellingPlanForms(sellingPlanFormsForUnselectedVariants);
  }

  handleVariantChange() {
    this.handleSellingPlanFormVisibility();
    this.updateSellingPlanInputsValues();
    this.listenToSellingPlanFormRadioButtonChange();
    this.updatePrice();
  }

  listenToVariantChange() {
    this.listenToAddToCartForms();
    if (this.variantIdInput?.tagName === "INPUT") {
      const variantIdObserver = new MutationObserver((mutationList) => {
        mutationList.forEach((mutationRecord) => {
          this.handleVariantChange();
        });
      });

      variantIdObserver.observe(this.variantIdInput, {
        attributes: true,
      });
    }
  }

  listenToAddToCartForms() {
    this.addToCartForm?.addEventListener("change", () => {
      this.handleVariantChange();
    });
  }

  get regularPriceElement() {
    return this.shopifySection?.querySelector(".price__regular");
  }

  get salePriceElement() {
    return this.shopifySection?.querySelector(".price__sale");
  }

  get salePriceValue() {
    return this.salePriceElement?.querySelector(".price-item--sale");
  }

  get regularPriceValue() {
    return this.salePriceElement?.querySelector(".price-item--regular");
  }

  get selectedPurchaseOptionPrice() {
    return this.selectedPurchaseOption?.dataset.variantPrice;
  }

  get selectedPurchaseOptionComparedAtPrice() {
    return this.selectedPurchaseOption?.dataset.variantCompareAtPrice;
  }

  get selectedPurchaseOption() {
    return this.visibleSellingPlanForm?.querySelector(
      'input[type="radio"]:checked'
    );
  }

  set selectedPurchaseOption(selectedPurchaseOption) {
    this._selectedPurchaseOption = selectedPurchaseOption;
  }

  // Set subscription as default on page load
  setDefaultSubscription() {
    const subscriptionRadio = this.visibleSellingPlanForm?.querySelector(
      'input[type="radio"].subscription-input'
    );
    if (subscriptionRadio && !this.selectedPurchaseOption) {
      subscriptionRadio.checked = true;
    }
  }

  updatePrice() {
    if (!this.selectedPurchaseOption) return;

    const hasDiscount = this.selectedPurchaseOptionComparedAtPrice &&
      this.selectedPurchaseOptionComparedAtPrice !== this.selectedPurchaseOptionPrice;

    if (!hasDiscount) {
      this.showRegularPrice();
      this.hideSalePrice();
      this.priceElement?.classList.remove("price--on-sale");
      this.hideSavingsBadge();
    } else {
      this.showSalePrice();
      this.hideRegularPrice();
      this.priceElement?.classList.add("price--on-sale");
      this.updateSavingsBadge();
    }
  }

  updateSavingsBadge() {
    const priceSavings = this.shopifySection?.querySelector(".price__savings");
    const saveText = this.shopifySection?.querySelector(".price__save-text");
    const badgeWrapper = this.shopifySection?.querySelector(".price__sale-badge-wrapper");

    if (priceSavings && this.selectedPurchaseOptionComparedAtPrice && this.selectedPurchaseOptionPrice) {
      const comparePrice = parseFloat(
        this.selectedPurchaseOptionComparedAtPrice.replace(/[^0-9.-]+/g, "")
      );
      const currentPrice = parseFloat(
        this.selectedPurchaseOptionPrice.replace(/[^0-9.-]+/g, "")
      );
      const savings = comparePrice - currentPrice;
      priceSavings.textContent = Math.round((savings + Number.EPSILON) * 100) / 100;
    }

    if (saveText) {
      const isSubscription = this.selectedPurchaseOption?.classList.contains("subscription-input");
      saveText.textContent = isSubscription ? "SUBSCRIBE & SAVE $" : "SAVE $";
    }

    if (badgeWrapper) {
      badgeWrapper.style.display = "block";
    }
  }

  hideSavingsBadge() {
    const badgeWrapper = this.shopifySection?.querySelector(".price__sale-badge-wrapper");
    if (badgeWrapper) {
      badgeWrapper.style.display = "none";
    }
  }

  hideSalePrice() {
    if (this.salePriceElement) {
      this.salePriceElement.style.display = "none";
    }
  }

  hideRegularPrice() {
    if (this.regularPriceElement) {
      this.regularPriceElement.style.display = "none";
    }
  }

  showRegularPrice() {
    if (this.regularPriceElement) {
      this.regularPriceElement.style.display = "block";
    }
    if (this.salePriceElement) {
      this.salePriceElement.style.display = "none";
    }
  }

  showSalePrice() {
    if (this.salePriceElement) {
      this.salePriceElement.style.display = "block";
    }
    if (this.regularPriceElement) {
      this.regularPriceElement.style.display = "none";
    }
    if (this.regularPriceValue && this.selectedPurchaseOptionComparedAtPrice) {
      this.regularPriceValue.textContent = this.selectedPurchaseOptionComparedAtPrice;
    }
    if (this.salePriceValue && this.selectedPurchaseOptionPrice) {
      this.salePriceValue.textContent = this.selectedPurchaseOptionPrice;
    }
  }

  get sellingPlanInputs() {
    return this.shopifySection?.querySelectorAll(".selected-selling-plan-id") || [];
  }

  updateSellingPlanInputsValues() {
    this.sellingPlanInputs.forEach((sellingPlanInput) => {
      sellingPlanInput.value = this.sellingPlanInputValue;
    });

    // Appstle integration
    const appstle = document.querySelector(".appstle_sub_widget");
    if (!appstle) return;
    
    if (this.sellingPlanInputValue) {
      const subscriptionOption = appstle.querySelector(
        ".appstle_subscription_wrapper_option:first-child input"
      );
      subscriptionOption?.click();
    } else {
      const oneTimeOption = appstle.querySelector(
        ".appstle_subscription_wrapper_option:last-child input"
      );
      oneTimeOption?.click();
    }
  }

  get sellingPlanInputValue() {
    return this.selectedPurchaseOption?.dataset.sellingPlanId ?? null;
  }

  handleRadioButtonChange(selectedPurchaseOption) {
    this.selectedPurchaseOption = selectedPurchaseOption;
    this.updateSellingPlanInputsValues();
    this.updatePrice();
  }

  listenToSellingPlanFormRadioButtonChange() {
    this.visibleSellingPlanForm
      ?.querySelectorAll('input[type="radio"]')
      .forEach((radio) => {
        radio.addEventListener("change", (event) => {
          this.handleRadioButtonChange(event.target);
        });
      });
  }

  enablePerformanceObserver() {
    const performanceObserver = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        if (entry.initiatorType !== "fetch") return;

        const url = new URL(entry.name);
        if (url.search.includes("variant") || url.search.includes("variants")) {
          // Small delay to ensure DOM is updated
          setTimeout(() => this.updatePrice(), 100);
        }
      });
    });

    performanceObserver.observe({ entryTypes: ["resource"] });
  }
}

// Initialize with delay to ensure DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initSellingPlans);
} else {
  initSellingPlans();
}

function initSellingPlans() {
  document
    .querySelectorAll(".selling_plan_app_container")
    .forEach((sellingPlansWidgetContainer) => {
      new SellingPlansWidget(sellingPlansWidgetContainer);
    });
}